import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Flag, Send, X } from 'lucide-react';
import { useAIFeedback } from '../../hooks/core/useAIFeedback';
import { logCallableFailure } from '../../lib/logCallableFailure';

/**
 * Props for AIFeedbackWidget component
 */
export interface AIFeedbackWidgetProps {
  /** Unique identifier for the AI output being rated */
  outputId: string;
  /** Optional callback fired after successful feedback submission */
  onFeedbackSubmit?: () => void;
}

type FeedbackType = 'helpful' | 'not_helpful' | 'flagged';

/**
 * AIFeedbackWidget - Reusable feedback buttons for AI-generated content
 *
 * Displays thumbs up/down and flag buttons to collect user feedback on AI outputs.
 * After clicking a button, shows a textarea for optional comments before submitting.
 */
export default function AIFeedbackWidget({ outputId, onFeedbackSubmit }: AIFeedbackWidgetProps) {
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { mutate: submitFeedback, isPending } = useAIFeedback();

  const handleSelect = (type: FeedbackType) => {
    setSelectedType(type);
    setReason('');
  };

  const handleCancel = () => {
    setSelectedType(null);
    setReason('');
  };

  const handleSubmit = () => {
    if (!selectedType) return;
    submitFeedback(
      { outputId, feedbackType: selectedType, comment: reason.trim() || undefined },
      {
        onSuccess: () => {
          setSubmitted(true);
          onFeedbackSubmit?.();
        },
        onError: (error) => {
          logCallableFailure('Failed to submit feedback:', error);
          setSelectedType(null);
          setReason('');
        },
      }
    );
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <span>Thanks for your feedback!</span>
      </div>
    );
  }

  if (selectedType) {
    const labels: Record<FeedbackType, string> = {
      helpful: 'What was helpful?',
      not_helpful: 'What could be improved?',
      flagged: 'What\'s the issue?',
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600">{labels[selectedType]}</span>
          <button
            onClick={handleCancel}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Cancel feedback"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Add details (optional)..."
          rows={2}
          maxLength={2000}
          className="w-full text-sm border border-slate-300 rounded-xl shadow-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          autoFocus
        />
        <button
          onClick={handleSubmit}
          disabled={isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Send className="w-3 h-3" />
          {isPending ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500">Was this helpful?</span>
      <button
        onClick={() => handleSelect('helpful')}
        disabled={isPending}
        className="p-1.5 rounded hover:bg-green-50 text-slate-400 hover:text-green-600 transition-colors disabled:opacity-50"
        aria-label="Helpful"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      <button
        onClick={() => handleSelect('not_helpful')}
        disabled={isPending}
        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
        aria-label="Not helpful"
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
      <button
        onClick={() => handleSelect('flagged')}
        disabled={isPending}
        className="p-1.5 rounded hover:bg-yellow-50 text-slate-400 hover:text-yellow-600 transition-colors disabled:opacity-50"
        aria-label="Flag issue"
      >
        <Flag className="w-4 h-4" />
      </button>
    </div>
  );
}
